import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import {isReleasedRedesignTop} from '../config/unReleasePage';
import {ReportType} from '../core/entities/reports/constants';
import {getTopCompanyList} from '../core/repositories/companyRepository';
import {
  type ReportsResponseType,
  getReports,
} from '../core/repositories/reportsRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../core/repositories/usersMeRepository';
import {validateCookie} from '../core/usecases/auth/checkOauthTokenExistence';
import {LoggedInTop} from '../features/top/components/Container/LoggedInTop';
import {LoggedInTopLayout} from '../features/top/components/Container/LoggedInTopLayout';
import {LoggedInTopRecommendPage} from '../features/top/components/Container/LoggedInTopRecommendPage';
import {NoLoggedInTop} from '../features/top/components/Container/NoLoggedInTop';
import {NAV_VALUE} from '../features/top/components/Container/Presenter/Navigation/constants';
import {withHandler} from '../server/token/withHandler';
import {ScriptForGA} from '../shared/components/ScriptForGA';
import {NavigationLayout} from '../shared/components/layouts/NavigationLayout';
import {NoLeftSidebarLayout} from '../shared/components/layouts/NoLeftSidebarLayout';
import type {NextPageWithLayout} from './_app';

type StaticCompany = {
  id: number;
  companyName: string;
};

type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  isValid: boolean;
  reportsData?: ReportsResponseType;
  staticCompanies: StaticCompany[];
  topCompanies?: {
    companies: Array<{
      id: number;
      name: string;
      logo_url: string;
      background_image_url: string;
      catch_phrase: string;
    }>;
  };
  isReleasedRedesignTop: boolean;
  isStudentVerificationRequired: boolean;
};

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession'
>(
  {auth: 'optional'},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);

    // SEOの観点からサーバーサイドで定義
    const staticCompanies = [
      {id: 1, companyName: 'マッキンゼー・アンド・カンパニー'},
      {id: 3, companyName: 'ボストンコンサルティンググループ'},
      {id: 13, companyName: '野村総合研究所'},
      {id: 21, companyName: 'ゴールドマン・サックス'},
      {id: 16, companyName: 'モルガン・スタンレー'},
      {id: 26, companyName: 'JPモルガン'},
      {id: 15, companyName: 'バークレイズ'},
      {id: 52, companyName: 'P&Gジャパン'},
      {id: 50, companyName: 'ユニリーバ'},
      {id: 49, companyName: '日本ロレアル'},
      {id: 256, companyName: 'ネスレ日本'},
      {id: 37, companyName: '三菱商事'},
      {id: 36, companyName: '伊藤忠商事'},
      {id: 38, companyName: '三井物産'},
      {id: 81, companyName: '電通'},
      {id: 82, companyName: '博報堂／博報堂DYメディアパートナーズ'},
      {id: 43, companyName: 'Google'},
      {id: 46, companyName: '日本IBM'},
      {id: 783, companyName: '外務省'},
      {id: 233, companyName: '経済産業省'},
      {id: 139, companyName: '国際協力機構（JICA）'},
      {id: 66, companyName: '日本銀行'},
      {id: 58, companyName: '三菱UFJ銀行'},
      {id: 63, companyName: '東京海上日動火災保険'},
      {id: 32, companyName: '野村證券'},
      {id: 77, companyName: '三菱地所'},
      {id: 76, companyName: '三井不動産'},
    ];

    const props: Props = {
      isValid,
      staticCompanies,
      isReleasedRedesignTop: isReleasedRedesignTop(),
      isStudentVerificationRequired: context.isStudentVerificationRequired,
    };

    if (!isReleasedRedesignTop()) {
      // トップ企業一覧の取得
      const topCompaniesResponse = await getTopCompanyList({
        cookies,
        queryParams: {
          limit: 20,
          is_only_top: true,
        },
      });
      if (topCompaniesResponse.ok) {
        props.topCompanies = topCompaniesResponse.value;
      } else {
        console.error(
          'トップ企業一覧の取得に失敗:',
          topCompaniesResponse.error,
        );
      }
    }

    if (!isReleasedRedesignTop()) {
      // SEOの観点からサーバーサイドで取得
      const reportsResponse = await getReports({
        queryParams: {
          limit: 8,
          types: [ReportType.SELECTION],
          departmentMasterIDs: [],
          page: 0,
        },
      });

      if (reportsResponse.ok) {
        props.reportsData = reportsResponse.value;
      }
    }

    if (!isValid) return {props};

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props,
      };
    }

    return {
      props: {
        ...props,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

// トップ画面
const Page: NextPageWithLayout<Props> = ({
  userDataForAnalytics,
  isValid,
  reportsData,
  staticCompanies,
  topCompanies,
  isReleasedRedesignTop,
  isStudentVerificationRequired,
}: Props) => {
  return (
    <div>
      {isValid ? (
        <>
          {isReleasedRedesignTop ? (
            <NavigationLayout
              navValue={NAV_VALUE.RECOMMEND}
              showSp
              withGoogleAd
              paddingRightZero
            >
              <LoggedInTopRecommendPage
                isStudentVerificationRequired={isStudentVerificationRequired}
              />
            </NavigationLayout>
          ) : (
            <LoggedInTopLayout>
              <LoggedInTop />
            </LoggedInTopLayout>
          )}
        </>
      ) : (
        <NoLeftSidebarLayout>
          <NoLoggedInTop
            reportsData={reportsData}
            staticCompanies={staticCompanies}
            topCompanies={topCompanies}
            isReleasedRedesignTop={isReleasedRedesignTop}
          />
        </NoLeftSidebarLayout>
      )}
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </div>
  );
};

Page.getLayout = (page) => <div>{page}</div>;

export default Page;
